import React, { useEffect, useMemo, useState } from "react";
import { FaCheckCircle, FaClock, FaFileAlt, FaMoneyCheckAlt, FaReceipt, FaTimesCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useAppTheme } from "../context/AppThemeContext";
import {
  formatCurrency,
  getNextAction,
  getStatusTone,
  getTimelineSteps,
  normalizeAdmissionRecord,
} from "../utils/admissionWorkflow";

const buildFallbackRecord = () => ({
  firstName: "Raam",
  lastName: "Patil",
  fatherName: "Shivraj Patil",
  motherName: "Sunita Patil",
  contactNumber: "9876543210",
  email: "nishi@example.com",
  address: "Badami, Karnataka",
  standard: "10th",
  admissionAmount: 1000,
  admissionDate: "2026-04-17",
  paymentId: "pay_SAMPLE123",
  receiptNumber: "RCT-2026-0001",
  status: "PAYMENT_SUCCESS",
});

export default function ViewAdmissionForm() {
  const { isDarkMode } = useAppTheme();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("api");

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const res = await api.get(API_PATHS.admission.myForm);

        const payload = res?.data?.data ?? res?.data ?? null;
        setFormData(payload ? normalizeAdmissionRecord(payload) : null);
        setSource("api");
      } catch {
        setFormData(normalizeAdmissionRecord(buildFallbackRecord()));
        setSource("fallback");
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, []);

  const timelineSteps = useMemo(
    () => getTimelineSteps(formData?.status || "DRAFT"),
    [formData]
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          Loading your admission workspace...
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900">No admission form found</h2>
          <p className="mt-2 text-sm text-slate-500">Start a new application to submit your admission details.</p>
          <Link
            to="/admission-form"
            className="mt-5 inline-flex rounded-xl bg-gradient-to-r from-blue-700 to-teal-600 px-5 py-3 text-sm font-semibold text-white"
          >
            Open admission form
          </Link>
        </div>
      </div>
    );
  }

  const statusTone = getStatusTone(formData.status);
  const nextAction = getNextAction(formData.status);
  const pageShellClass = isDarkMode ? "bg-slate-950" : "bg-transparent";
  const heroClass = isDarkMode ? "border-slate-800 bg-slate-900 shadow-[0_22px_55px_-34px_rgba(2,6,23,0.75)]" : "border-slate-200 bg-white shadow-[0_22px_55px_-34px_rgba(15,23,42,0.26)]";
  const headingClass = isDarkMode ? "text-white" : "text-slate-900";
  const bodyTextClass = isDarkMode ? "text-slate-300" : "text-slate-600";
  const mutedTextClass = isDarkMode ? "text-slate-400" : "text-slate-500";
  const statusToneClass = isDarkMode
    ? formData.status.includes("SUCCESS") || formData.status === "COMPLETED" || formData.status === "RECEIPT_GENERATED"
      ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-100"
      : formData.status.includes("FAILED") || formData.status.includes("REJECTED")
        ? "border-rose-500/20 bg-rose-500/10 text-rose-100"
        : "border-blue-500/20 bg-blue-500/10 text-blue-100"
    : statusTone;

  return (
    <div className={`${pageShellClass}`}>
      <div className="mx-auto max-w-6xl px-4 py-8">
      <div className={`rounded-[28px] border p-6 ${heroClass}`}>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "border-blue-500/20 bg-blue-500/10 text-blue-200" : "border-blue-200 bg-blue-50 text-blue-700"}`}>
              <FaFileAlt /> Admission
            </div>
            <h1 className={`mt-4 text-3xl font-black tracking-tight sm:text-4xl ${headingClass}`}>
              {`${formData.firstName || "Student"} ${formData.lastName || ""}`.trim()}
            </h1>
            <p className={`mt-2 text-sm ${bodyTextClass}`}>
              Review status, payment, and receipt.
            </p>
            {source === "fallback" ? (
              <p className={`mt-3 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${isDarkMode ? "border-amber-500/20 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                Fallback data
              </p>
            ) : null}
          </div>
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${statusToneClass}`}>
            {formData.status}
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-4">
          <SummaryCard isDarkMode={isDarkMode} icon={FaCheckCircle} label="Stage" value={formData.status.replaceAll("_", " ")} note={nextAction} />
          <SummaryCard isDarkMode={isDarkMode} icon={FaMoneyCheckAlt} label="Paid" value={formatCurrency(formData.finalAmount || formData.admissionAmount, formData.currency)} note={formData.paymentId ? "confirmed" : "pending"} />
          <SummaryCard isDarkMode={isDarkMode} icon={FaReceipt} label="Receipt" value={formData.receiptNumber || "Pending"} note={formData.receiptNumber ? "available" : "not ready"} />
          <SummaryCard isDarkMode={isDarkMode} icon={FaClock} label="Date" value={formData.admissionDate || "Not available"} note={formData.standard ? `std ${formData.standard}` : ""} />
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-5">
          {timelineSteps.map((step) => (
            <div key={step.key} className={`rounded-2xl border px-4 py-3 text-sm ${step.done ? (isDarkMode ? "border-blue-500/20 bg-blue-500/10" : "border-blue-200 bg-blue-50") : (isDarkMode ? "border-slate-800 bg-slate-950/60" : "border-slate-200 bg-slate-50")}`}>
              <div className={`flex items-center gap-2 font-semibold ${headingClass}`}>
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${step.done ? "bg-blue-600 text-white" : (isDarkMode ? "border border-slate-700 bg-slate-900 text-slate-400" : "border border-slate-200 bg-white text-slate-500")}`}>
                  {step.done ? <FaCheckCircle /> : "•"}
                </span>
                {step.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          <PanelCard isDarkMode={isDarkMode} title="Applicant details">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Detail label="First Name" value={formData.firstName} />
              <Detail label="Last Name" value={formData.lastName} />
              <Detail label="Father's Name" value={formData.fatherName} />
              <Detail label="Mother's Name" value={formData.motherName} />
              <Detail label="Contact Number" value={formData.contactNumber} />
              <Detail label="Email" value={formData.email} />
              <Detail label="Address" value={formData.address} />
              <Detail label="Standard" value={formData.standard} />
              <Detail label="Admission Amount" value={formatCurrency(formData.admissionAmount, formData.currency)} />
              <Detail label="Admission Date" value={formData.admissionDate} />
            </div>
          </PanelCard>

          <PanelCard isDarkMode={isDarkMode} title="Payment and receipt">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Detail label="Base amount" value={formatCurrency(formData.baseAmount || formData.admissionAmount, formData.currency)} />
              <Detail label="Waiver or discount" value={formatCurrency(formData.waiverAmount || 0, formData.currency)} />
              <Detail label="Final amount" value={formatCurrency(formData.finalAmount || formData.admissionAmount, formData.currency)} />
              <Detail label="Payment ID" value={formData.paymentId || "Pending"} />
              <Detail label="Receipt number" value={formData.receiptNumber || "Pending"} />
              <Detail label="Status" value={formData.status.replaceAll("_", " ")} />
            </div>
          </PanelCard>
        </div>

        <aside className="space-y-4">
          <PanelCard isDarkMode={isDarkMode} title="Next action">
            <p className={`text-sm leading-6 ${bodyTextClass}`}>{nextAction}</p>
            {formData.rejectionReason ? (
              <div className={`mt-4 rounded-2xl border p-4 text-sm ${isDarkMode ? "border-rose-500/20 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                <div className="flex items-start gap-2 font-semibold">
                  <FaTimesCircle className="mt-0.5" /> Review note
                </div>
                <p className="mt-2 leading-6">{formData.rejectionReason}</p>
              </div>
            ) : null}
          </PanelCard>

          <PanelCard isDarkMode={isDarkMode} title="Quick actions">
            <div className="space-y-2">
              <Link
                to="/admission-form"
                className={`block rounded-xl border px-4 py-3 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                Admission form
              </Link>
              <Link
                to="/user-dashboard"
                className={`block rounded-xl border px-4 py-3 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                Dashboard
              </Link>
            </div>
          </PanelCard>
        </aside>
      </div>
      </div>
    </div>
  );
}

const PanelCard = ({ title, children, isDarkMode }) => (
  <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
    <h2 className={`text-sm font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{title}</h2>
    <div className="mt-4">{children}</div>
  </div>
);

const SummaryCard = ({ icon: Icon, label, value, note, isDarkMode }) => (
  <div className={`rounded-2xl border px-4 py-4 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-50"}`}>
    <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
      <Icon className="text-blue-600" /> {label}
    </div>
    <p className={`mt-3 text-xl font-black ${isDarkMode ? "text-white" : "text-slate-900"}`}>{value}</p>
    <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{note}</p>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 app-theme-dark:border-slate-800 app-theme-dark:bg-slate-950/60">
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 app-theme-dark:text-slate-400">{label}</p>
    <p className="mt-2 text-sm font-semibold text-slate-900 app-theme-dark:text-slate-100">{value || "Not available"}</p>
  </div>
);
